/**
 * Where an item's current route came from, in words (review 2r8pgd: for an
 * item the system assigned and never emailed, the owner wanted to know
 * "where it was requested from", not a button to send it).
 *
 * The backend reads the audit trail and says who assigned it, by which
 * path, when, and from whom (`route_origin` on the thread). This file only
 * phrases it. Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead, RouteOrigin } from '../../services/qcpInvestigations';
import { shortDate } from '../../components/portal/threadTriageView';

type WithOrigin = Partial<Pick<DoorThreadRead, 'route_origin' | 'routed_to'>>;

/** "Merlin's nightly router" / "Jess Finlaw" / "a direct database write". */
export function originActor(origin: RouteOrigin): string {
  switch (origin.via) {
    case 'nightly_router':
      return "Merlin's nightly router";
    case 'merlin':
      return 'Merlin';
    case 'database':
      return 'a direct database write';
    default:
      return origin.actor_label.trim() || 'someone';
  }
}

/** "from the Quality door" / "from the admin Quality Routing page" / "". */
function viaPhrase(origin: RouteOrigin): string {
  switch (origin.via) {
    case 'quality_door':
      return 'from the Quality door';
    case 'admin_routing_page':
      return 'from the admin Quality Routing page';
    case 'nightly_router':
      return 'by writing the route straight to the database';
    default:
      return '';
  }
}

function onDay(origin: RouteOrigin): string {
  const day = shortDate(origin.at);
  return day ? ` on ${day}` : '';
}

/**
 * One sentence for the item header: who assigned it, how, when, from whom,
 * and what that path means for the people named.
 */
export function whereFromLine(thread: WithOrigin): string {
  const origin = thread.route_origin;
  const to = (thread.routed_to ?? '').trim() || 'them';
  if (!origin) return 'The audit trail holds no line that says who assigned this.';
  const who = originActor(origin);
  const via = viaPhrase(origin);
  const when = onDay(origin);
  const from = origin.previous_routee ? `, taking it from ${origin.previous_routee}` : '';
  let line: string;
  if (origin.born_routed) {
    line = `${who} created this item already assigned to ${to}${when}${via ? `, ${via}` : ''}.`;
  } else {
    line = `${who} assigned this to ${to}${when}${via ? ` ${via}` : ''}${from}.`;
  }
  if (origin.via === 'nightly_router') {
    line += ' No person chose this, and that path sends no hand-off email.';
  }
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The short form for a list row: "Assigned by Merlin's nightly router on Oct 6, not by a person." */
export function assignedByLine(thread: WithOrigin): string {
  const origin = thread.route_origin;
  if (!origin) return 'Who assigned it is not on record.';
  const who = originActor(origin);
  const when = onDay(origin);
  const byMachine = origin.actor_kind !== 'human';
  return `Assigned by ${who}${when}${byMachine ? ', not by a person' : ''}.`;
}

/**
 * "by Merlin's nightly router" for the "Why it went to them · sent by …"
 * label when the timeline has no routing line to read; null without a
 * record.
 */
export function assignedByShort(thread: WithOrigin): string | null {
  const origin = thread.route_origin;
  if (!origin) return null;
  return originActor(origin);
}
